import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useLanguage } from '../../contexts/LanguageContext';
import { StockIssue } from '../../types/stockIssues.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface StockIssuePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  issue: StockIssue | null;
  onEdit?: () => void;
}

const StockIssuePreviewDialog: React.FC<StockIssuePreviewDialogProps> = ({
  open,
  onClose,
  issue,
  onEdit
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!issue) return null;

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w || !issue) return;
    const themeColor = '#667eea';
    const totalValue = issue.details?.reduce((s, d) => s + (d.totalCost || d.quantity * d.unitCost), 0) || 0;
    w.document.write(`<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8">
<title>${issue.issueNumber}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Arial,sans-serif; padding:25px; font-size:12px; direction:${isRTL ? 'rtl' : 'ltr'}; }
  .header { text-align:center; border-bottom:3px solid ${themeColor}; padding-bottom:12px; margin-bottom:15px; }
  .header img { max-height:55px; margin-bottom:5px; }
  .header h2 { color:${themeColor}; margin:3px 0; }
  .doc-title { text-align:center; margin-bottom:15px; }
  .doc-title h3 { color:${themeColor}; border:2px solid ${themeColor}; display:inline-block; padding:6px 25px; border-radius:6px; }
  .info { display:grid; grid-template-columns:1fr 1fr; gap:8px; border:1px solid #ddd; border-radius:6px; padding:12px; margin-bottom:15px; }
  .info-item label { font-size:10px; color:#666; display:block; } .info-item span { font-weight:bold; }
  .section { background:${themeColor}; color:white; padding:6px 12px; font-weight:bold; font-size:11px; }
  table { width:100%; border-collapse:collapse; } th { background:#f5f5f5; padding:6px; font-size:11px; border:1px solid #ddd; text-align:${isRTL?'right':'left'}; }
  td { padding:5px 6px; border:1px solid #eee; font-size:11px; } .amount { text-align:${isRTL?'left':'right'}; font-weight:bold; }
  .total-row td { background:${themeColor}; color:white; font-weight:bold; font-size:12px; }
  .signatures { display:flex; justify-content:space-between; margin-top:50px; }
  .sig-box { text-align:center; width:30%; } .sig-line { border-top:1px solid #333; margin-top:50px; padding-top:6px; font-weight:bold; }
  @media print { @page { size:A4; margin:1cm; } }
</style></head><body>
${companyInfo ? `<div class="header">${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}<h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2><div style="font-size:10px;color:#666">${companyInfo.address||''} ${companyInfo.phone ? '| '+companyInfo.phone : ''} ${companyInfo.email ? '| '+companyInfo.email : ''}</div></div>` : ''}
<div class="doc-title"><h3>${isRTL ? 'إذن صرف مخزني' : 'Stock Issue'}</h3></div>
<div class="info">
  <div class="info-item"><label>${isRTL?'رقم الإذن':'Issue No.'}</label><span>${issue.issueNumber}</span></div>
  <div class="info-item"><label>${isRTL?'التاريخ':'Date'}</label><span>${formatDate(issue.issueDate)}</span></div>
  <div class="info-item"><label>${isRTL?'المخزن المصدر':'Source Warehouse'}</label><span>${isRTL ? issue.warehouseNameAr : issue.warehouseName}</span></div>
  <div class="info-item"><label>${isRTL?'الفرع الوجهة':'Destination Branch'}</label><span>${isRTL ? issue.branchNameAr : issue.branchName}</span></div>
  <div class="info-item"><label>${isRTL?'الحالة':'Status'}</label><span>${getStatusLabel(issue.status)}</span></div>
  ${issue.notes ? `<div class="info-item"><label>${isRTL?'ملاحظات':'Notes'}</label><span>${issue.notes}</span></div>` : ''}
</div>
<div class="section">${isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}</div>
<table><thead><tr><th>#</th><th>${isRTL?'الصنف':'Item'}</th><th>${isRTL?'الوحدة':'Unit'}</th><th class="amount">${isRTL?'الكمية':'Qty'}</th><th class="amount">${isRTL?'التكلفة':'Cost'}</th><th class="amount">${isRTL?'الإجمالي':'Total'}</th></tr></thead><tbody>
${(issue.details||[]).map((d: any, i: number) => `<tr><td>${i+1}</td><td>${isRTL ? (d.itemNameAr||d.itemName) : d.itemName}</td><td>${d.unitName||''}</td><td class="amount">${d.quantity}</td><td class="amount">${(d.unitCost||0).toFixed(3)}</td><td class="amount">${(d.totalCost || d.quantity * d.unitCost).toFixed(3)}</td></tr>`).join('')}
<tr class="total-row"><td colspan="5" style="text-align:${isRTL?'left':'right'}">${isRTL?'الإجمالي':'Total'}</td><td class="amount">${totalValue.toFixed(3)} KD</td></tr>
</tbody></table>
<div class="signatures">
  <div class="sig-box"><div class="sig-line">${isRTL?'أمين المخزن':'Warehouse Keeper'}</div></div>
  <div class="sig-box"><div class="sig-line">${isRTL?'مدير الفرع':'Branch Manager'}</div></div>
  <div class="sig-box"><div class="sig-line">${isRTL?'المحاسب':'Accountant'}</div></div>
</div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Draft':
        return isRTL ? 'مسودة' : 'Draft';
      case 'Posted':
        return isRTL ? 'مرحّل' : 'Posted';
      default:
        return status || '-';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Draft':
        return 'default';
      case 'Posted':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.000';
    return amount.toFixed(3);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        dir={isRTL ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDarkMode ? '#1a1a2e' : 'white'
          }
        }}
      >
        <DialogTitle
          className="no-print"
          sx={{
            background: isDarkMode
              ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon />
            <Box component="span">
              {isRTL ? 'معاينة إصدار مخزون' : 'Stock Issue Preview'}
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          <Box className="print-content-wrapper">
          {/* Company Header - Print Only */}
          {companyInfo && (
            <Box className="print-only" sx={{ display: 'none', mb: 2, textAlign: 'center', borderBottom: '2px solid #667eea', pb: 2 }}>
              {companyInfo.logoPath && (
                <Box 
                  component="img" 
                  src={`${config.API_ORIGIN}${companyInfo.logoPath}`} 
                  alt="Company Logo" 
                  sx={{ maxHeight: 50, mb: 1 }} 
                  onError={(e: any) => { e.target.style.display = 'none'; }}
                />
              )}
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
                {isRTL ? companyInfo.companyNameAr : companyInfo.companyName}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
                {companyInfo.address && `${companyInfo.address} | `}
                {companyInfo.phone && `${companyInfo.phone} | `}
                {companyInfo.email}
                {companyInfo.website && ` | ${companyInfo.website}`}
              </Typography>
            </Box>
          )}

          {/* Document Type Header - Print Only */}
          <Box className="print-only" sx={{ display: 'none', mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#667eea' }}>
              {isRTL ? 'إصدار مخزون' : 'Stock Issue'}
            </Typography>
          </Box>

          {/* Header Info */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
              borderRadius: 2,
              '@media print': {
                border: '2px solid #667eea',
                borderRadius: 0
              }
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم الإصدار' : 'Issue Number'}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {issue.issueNumber}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'التاريخ' : 'Date'}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatDate(issue.issueDate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'الحالة' : 'Status'}
                </Typography>
                <Box>
                  <Chip
                    label={getStatusLabel(issue.status)}
                    color={getStatusColor(issue.status)}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </Box>
              {issue.journalEntryId && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {isRTL ? 'رقم القيد' : 'Journal Entry'}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="success.main">
                    {issue.journalEntryNumber || ''} (#{issue.journalEntryId})
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Warehouse Info */}
            <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(102,126,234,0.1)' : '#e8eaf6', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'المخزن المصدر' : 'Source Warehouse'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {isRTL ? issue.warehouseNameAr : issue.warehouseName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {issue.warehouseCode}
              </Typography>
            </Box>

            {/* Branch Info */}
            <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الفرع الوجهة' : 'Destination Branch'}
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                {isRTL ? issue.branchNameAr : issue.branchName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {issue.branchCode}
              </Typography>
            </Box>

            {issue.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                  {isRTL ? 'ملاحظات:' : 'Notes:'}
                </Typography>
                <Typography variant="body2">
                  {issue.notes}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Items Details */}
          <Paper
            elevation={2}
            sx={{
              mb: 2,
              borderRadius: 2,
              overflow: 'hidden',
              '@media print': {
                border: '1px solid #667eea',
                borderRadius: 0
              }
            }}
          >
            <Box
              sx={{
                bgcolor: isDarkMode ? 'rgba(102,126,234,0.3)' : '#667eea',
                color: 'white',
                p: 1.5,
                fontWeight: 'bold',
                '@media print': {
                  bgcolor: '#667eea !important',
                  color: 'white !important'
                }
              }}
            >
              {isRTL ? '📦 تفاصيل الأصناف' : '📦 Items Details'}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الصنف' : 'Item'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الوحدة' : 'Unit'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الكمية' : 'Quantity'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'التكلفة' : 'Unit Cost'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {isRTL ? 'الإجمالي' : 'Total'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {issue.details.map((detail, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        <strong>{detail.itemCode}</strong>
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {isRTL ? detail.itemNameAr : detail.itemName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {isRTL ? detail.unitNameAr : detail.unitName}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {formatCurrency(detail.quantity)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {formatCurrency(detail.unitCost)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'primary.main' }}>
                        {formatCurrency(detail.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Total */}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '1rem',
                      bgcolor: isDarkMode ? 'rgba(102,126,234,0.2)' : 'rgba(102,126,234,0.1)'
                    }}>
                      {isRTL ? 'الإجمالي الكلي:' : 'Total:'}
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '1.1rem',
                      color: 'success.main',
                      bgcolor: isDarkMode ? 'rgba(102,126,234,0.2)' : 'rgba(102,126,234,0.1)'
                    }}>
                      {formatCurrency(issue.details.reduce((sum, d) => sum + (d.totalCost || 0), 0))} {isRTL ? 'د.ك' : 'KD'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Signatures - Print Only */}
          <Box className="print-only" sx={{ display: 'none', mt: 5, pt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ textAlign: 'center', width: '30%' }}>
                <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 8 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {isRTL ? 'المعتمد' : 'Approved By'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center', width: '30%' }}>
                <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 8 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {isRTL ? 'أمين المخزن' : 'Warehouse Keeper'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center', width: '30%' }}>
                <Box sx={{ borderTop: '1px solid #000', pt: 1, mt: 8 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {isRTL ? 'المحاسب' : 'Accountant'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          </Box> {/* Close print-content-wrapper */}
        </DialogContent>

        <DialogActions className="no-print" sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
          <Button onClick={onClose} size="large">
            {isRTL ? 'إغلاق' : 'Close'}
          </Button>
          
          {issue?.status === 'Draft' && onEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                onEdit();
                onClose();
              }}
              size="large"
              color="primary"
            >
              {isRTL ? 'تعديل' : 'Edit'}
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="large"
            sx={{
              background: isDarkMode
                ? 'linear-gradient(45deg, #0f3460 30%, #16213e 90%)'
                : 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            }}
          >
            {isRTL ? 'طباعة' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            /* Hide everything on page */
            body > *:not(.MuiDialog-root) {
              display: none !important;
            }
            
            /* Reset body and html */
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
            }
            
            /* Make dialog take full page */
            .MuiDialog-root {
              position: static !important;
              width: 100% !important;
              height: auto !important;
            }
            
            .MuiBackdrop-root {
              display: none !important;
            }
            
            .MuiDialog-container {
              position: static !important;
              display: block !important;
              width: 100% !important;
              height: auto !important;
              transform: none !important;
            }
            
            .MuiPaper-root {
              margin: 0 !important;
              padding: 0 !important;
              max-width: 100% !important;
              width: 100% !important;
              height: auto !important;
              max-height: none !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              overflow: visible !important;
            }
            
            .MuiDialogContent-root {
              padding: 0 !important;
              margin: 0 !important;
              overflow: visible !important;
              max-height: none !important;
            }
            
            /* Add padding to content wrapper only */
            .print-content-wrapper {
              padding: 5mm !important;
            }
            
            /* Hide/Show elements */
            .no-print,
            .MuiDialogActions-root,
            .MuiDialogTitle-root {
              display: none !important;
            }
            
            .print-only {
              display: block !important;
            }
            
            /* Table print optimization */
            table {
              page-break-inside: auto !important;
              width: 100% !important;
            }
            
            tr {
              page-break-inside: avoid !important;
              page-break-after: auto !important;
            }
            
            thead {
              display: table-header-group !important;
            }
            
            tfoot {
              display: table-footer-group !important;
            }
            
            /* Page settings */
            @page {
              size: A4;
              margin: 0;
            }
          }
        `}
      </style>
    </>
  );
};

export default StockIssuePreviewDialog;